package org.multiplatform.kotfolio.pages.kotlin.new_feature

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Constants

@Page("/kotlin/new_feature/2.1.0")
@Composable
fun NewFeature_2_1_0Page() {
    BlogLayout(
        PageProperties(
            "Kotlin 2.1.0",
            ""
        ),
        BlogProperties("kotlin", "Kotlin 2.1.0")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Add Compiler Option in build.gradle.kts",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    kotlin {
                        compilerOptions {
                            freeCompilerArgs.add("-Xwhen-guards")
                            freeCompilerArgs.add("-Xmulti-dollar-interpolation")
                             extraWarnings.set(true)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Guard Conditions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    sealed interface Animal {
                        data class Cat(val mouseHunter: Boolean) : Animal {
                            fun feedCat() {}
                        }
                        
                        data class Dog(val breed: String) : Animal {
                            fun feedDog() {}
                        }
                    }

                    fun feedAnimal(animal: Animal){
                        when(animal) {
                            is Animal.Dog -> animal.feedDog()
                            
                            is Animal.Cat if !animal.mouseHunter -> animal.feedCat()
                            
                            else -> println("Unknown animal")
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    sealed interface Animal
                    data class Cat(val isHungry: Boolean) : Animal
                    data class Dog(val breed: String) : Animal

                    fun feedAnimal(animal: Animal) {
                        when (animal) {
                            is Dog -> println("Feed the dog")
                            is Cat if !animal.isHungry -> println("Cat is satisfied")
                            else -> println("Feed the animal")
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun render(status: Status): String = when (status) {
                        Status.Loading -> "loading"
                        is Status.Ok if status.info.isEmpty() -> "no data"
                        is Status.Ok -> status.info.joinToString()
                        is Status.Error if status.problem == Problem.CONNECTION ->
                            "problems with connection"
                        is Status.Error if status.problem == Problem.AUTHENTICATION ->
                            "could not be authenticated"
                        else -> "unknown problem"
                    }

                    // this one is harder to write with nesting, so guards improve the readibility
                    fun renderWithGuards(status: Status): String = when (status) {
                        Status.Loading -> "loading"
                        is Status.Ok if status.info.isNotEmpty() -> status.info.joinToString()
                        is Status.Error if status.isCritical -> "critical problem"
                        else -> "problem, try again"
                    }

                    sealed interface Status {
                        object Loading : Status
                        data class Ok(val info: List<String>) : Status
                        data class Error(val problem: Problem, val isCritical: Boolean = false) : Status
                    }

                    enum class Problem {
                        CONNECTION, AUTHENTICATION
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Goodbye to Nested If-Else Blocks
                    
                    fun goodHandleUserStatus (user: User) {
                        when (user) {
                            is User.Active if user.points > 100 -> printIn("Premium Active User")
                            is User.Active -> println("Standard Active User") 
                            is User.Inactive -> println("Account Inactive")
                        }
                    }        
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    fun checkValue(x: Int) {
                        when (x) {
                            in 1..10 -> when {
                                x % 2 == 0 -> println("$x is even")
                                else -> println("$x is odd")
                            }
                            else -> println("$x is out of range")
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    interface Reference<T> {
                        val id: Int
                        suspend fun get(): T?
                    }

                    abstract class Animal
                    data class ForestAnimal(val name: String): Animal()
                    sealed class FarmAnimal : Animal()
                    data class Cattle(val givesMilk: Boolean): FarmAnimal()
                    data class Sheep(val color: String): FarmAnimal()

                    suspend fun <T: FarmAnimal> Reference<T>.describe1(): String =
                        when (val animal = get()) {
                            null -> "No animal"
                            is Cattle -> if (animal.givesMilk) "Cow" else "Non-cow cattle"
                            is Sheep -> "Sheep of color ${animal.color}"
                            // before 'else' was needed here
                        }

                    // This is a nice example for guards too!
                    // The 'if' just "moves to the left"

                    suspend fun <T: FarmAnimal> Reference<T>.describe2(): String =
                        when (val animal = get()) {
                            null -> "No animal"
                            is Cattle if animal.givesMilk -> "Cow"
                            is Cattle -> "Non-cow cattle"
                            is Sheep -> "Sheep of color ${animal.color}"
                            // before 'else' was needed here
                        }

                    // -------

                    // Why would you want to write <T: FarmAnimal> somewhere?
                    // Let's introduce another class:

                    sealed interface Result
                    data class Success(val value: String, val warnings: List<String>): Result
                    data class Failure(val error: String, val warnings: List<String>): Result

                    fun addWarning1(result: Result, warning: String): Result =
                        when (result) {
                            is Success -> Success(result.value, result.warnings + warning)
                            is Failure -> Failure(result.error, result.warnings + warning)
                        }

                    val resultOriginal: Success = Success("ok", emptyList())
                    // note that the type is a bit "lost"
                    val result1 = addWarning1(resultOriginal, "warning")

                    fun <T: Result> addWarning2(result: T, warning: String): T =
                        when (result) {
                            is Success -> Success(result.value, result.warnings + warning)
                            is Failure -> Failure(result.error, result.warnings + warning)
                        } as T  // the unchecked cast here is slightly annoying...

                    // Now the type is correctly "threaded" through the code
                    val result2 = addWarning2(resultOriginal, "warning")
                    """.trimIndent()
            )

            SpanText(
                text = "Non-local break and continue",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    fun search(matrix: List<List<Int>>, target: Int): Boolean {
                        outer@ for (row in matrix) {
                            for (element in row) {
                                if (element == target) break@outer
                            }
                        }
                        // ...
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import java.io.File

                    // EXAMPLE 1: USE

                    // you start with this code, in which we gather
                    // the first line from a set of files
                    fun List<File>.readFirstLines1(): List<String> = buildList {
                        for (file in this@readFirstLines1) {
                            val reader = file.bufferedReader(Charsets.UTF_8)
                            val line = reader.readLine()
                            // here we use continue to remove the empty ones
                            if (line.isNullOrEmpty()) continue
                            else add(line)
                        }
                    }

                    // but then we realize: hey! we miss correct closing of the file
                    // for this in Kotlin we use 'use', but then...
                    fun List<File>.readFirstLines2(): List<String> = buildList {
                        for (file in this@readFirstLines2) {
                            file.bufferedReader(Charsets.UTF_8).use { reader ->
                                val line = reader.readLine()
                                // this is now a 'continue' inside a lambda
                                if (line.isNullOrEmpty()) continue
                                else add(line)
                            }
                        }
                    }

                    // EXAMPLE 2: WITH

                    interface User {
                        suspend fun isValid(): Boolean
                    }

                    suspend fun report1(users: List<User>) {
                        for (user in users) {
                            // do something with 'user'
                            // ...
                            // at some point
                            if (!user.isValid()) break
                            // continue with 'user'
                        }
                    }

                    // but since we are using 'user' a lot,
                    // we decide to make it implicit with 'with'

                    suspend fun report2(users: List<User>) {
                        for (user in users) {
                            with (user) {
                                // this is now a 'continue' inside a lambda
                                if (!isValid()) break
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Triple-Quoted Strings",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    val user = User("John", 5)
                    val message = ""$${'"'}
                        Hello, ${user.name}!
                        You have ${user.unreadMessages} unread messages.
                    ""$${'"'}
                    """.trimIndent()
            )

            SpanText(
                text = "Current Time Now",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val now = Instant.now() //Deprecated

                    @OptIn(ExperimentalTime::class)
                    val now = Clock.System.now()
                    """.trimIndent()
            )

            SpanText(
                text = "require",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    fun divide(a: Int, b: Int): Double {
                        require(b != 0) { "Denominator cannot be zero." }
                        return a.toDouble() / b
                    }

                    fun main(){
                        println(divide(10, 2))
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Multi-Dollar Interpolation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$$"""
                    val amount = 1000
                    val message = "$${amount} has been deposited."
                    """.trimIndent()
            )

            SpanText(
                text = "Return in Expression Body (Explicit Return Type)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    fun greet (name: String?): String = name ?: return "Anonymous"
                    """.trimIndent()
            )

            SpanText(
                text = "Safe Calls and Let",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    fun displayUserName(userName: String?) {
                        userName?.let {
                            when {
                                it.isEmpty() -> println("User name is empty.")
                                it.length > 10 -> println("User name is too long.")
                                else -> println("User name is: $it")
                            }
                        } ?: println("User name is null.")
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Kotlin 2.4 Rich Errors
                    
                    //1
                    fun fetchUser(): User | AppError {
                        if (/* network fails */ false) return NetworkError(503)
                        if (/* user not found */ false) return UserNotFoundError
                        return User("1234", "Kamlesh")
                    }

                    fun loadUserDataNew() {
                        val result = fetchUser()
                        when (result) {
                            is User -> show(result)
                            is NetworkError -> showError("Network issue (${result.code}). Try again.")
                            is UserNotFoundError -> showError("User not found. Please check your credentials.")
                        }
                    }
                    
                    //2
                    error object FetchingError
                    error class TransactionError(val errorMessage: String)

                    fun fetchUser(): User | FetchingError
                    fun User.charge(amount: Double): TransactionId | TransactionError
                    
                    val user = fetchUser()
                    val transaction = user?.charge(amount = 10.0)
                    when (transaction) {
                        is TransactionId -> println("Transaction succeeded")
                        is FetchingError -> println("Fetching failed")
                        is TransactionError -> 
                            println("Transaction failed: ${transaction.errorMessage}")
                    }
                    
                    //3
                    sealed class LoginResult {
                        data class Success(val token: String): LoginResult()
                        object InvalidCredentials: LoginResult()
                        object NetworkFailure: LoginResult()
                    }

                    fun login(email: String, password: String): LoginResult
                    
                    fun login(email: String, password: String): AuthToken | InvalidCredentials | NetworkFailure
                    
                    when (val result = login("email", "password")) {
                        is AuthToken -> navigateToHome(result)
                        is InvalidCredentials -> showError("Please try again")
                        is NetworkFailure -> showError("Please check your connection")
                    }
                    
                    //4
                    fun validateEmail(email: String): ValidEmail | EmailValidationError
                    
                    //5
                    fun readFile(path: String): FileContent | FileAccessError
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Kotlin 2.4 Position-based destructuring
                    
                    data class Talk(val title: String, val speakerName: String)
                    val talk = Talk("Kotlin Conf 2025", "Darren Le")

                    // Position-based destructuring
                    val (title, speakerName) = talk
                    print("title = $title, speakerName= $speakerName")  
                    // Output will be: title = Kotlin Conf 2025, speakerName = Darren Le
                    
                    //name-base destructuring
                    (val speakerName, val title) = talk
                    print("title = $title, speakerName= $speakerName")  
                    """.trimIndent()
            )
        }
    }
}





























